import { useDispatch, useSelector } from "react-redux";
import { Nav } from "../nav/Nav";
import { dashboardActions } from "../../../store/dashboard";
import { Bars3CenterLeftIcon } from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { EMAIL_KEY, EVENT_KEY } from "../../../pages/protected-route";
import { UserCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { AccountMenuContent } from "../nav/account-menu/AccountMenuContent";
import { CreateEventModal } from "../create-event/CreateEventModal";
import { useUserPermissions } from "../../../utils/auth";
import { PermissionDenied } from "../../PermissionDenied";
import {
  CategoryScale,
  Chart,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { State } from "../../../store/store";
import { motion } from "motion/react";
import { Bell } from "lucide-react";
import { ThemeProvider } from "@/components/portal/ThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";

interface Props {
  children: any;
  pageTitle?: string;
  rightControls?: any;
  requiresSuperuser?: boolean;
  requiredPermissions: Array<string>;
  workInProgress?: string;
}

Chart.register(PointElement, LinearScale, LineElement, CategoryScale, Tooltip);

export const DashboardLayout = ({
  children,
  pageTitle,
  rightControls,
  requiresSuperuser,
  requiredPermissions,
  workInProgress,
}: Props) => {
  const event = JSON.parse(localStorage.getItem(EVENT_KEY) || "{}");
  const dispatch = useDispatch();
  const [userPermissions, loading] = useUserPermissions();
  const organisationId = useSelector(
    (state: State) => state.dashboard.organisationId
  );
  const navHidden = useSelector((state: State) => state.dashboard.navHidden);

  const [isMounted, setIsMounted] = useState(false);

  // Delay mount until state is fully synchronized
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 10);

    return () => clearTimeout(timeout);
  }, []);

  if (!isMounted) {
    return null;
  }

  const setSidebarOpen = (sidebarOpen: boolean) => {
    dispatch(dashboardActions.SetSideBarOpen({ sidebarOpen }));
  };

  const currentPermissions = organisationId
    ? userPermissions[organisationId]?.permissions || []
    : [];
  const isOrganisationAdmin = organisationId
    ? userPermissions[organisationId]?.is_organisation_admin || false
    : false;

  return (
    <>
      <ThemeProvider>
        <TooltipProvider>
          {event && !event.isActive && (
            <div className="absolute z-50 w-full">
              <div className="bg-indigo-500 w-full h-1"></div>
              <div className="flex w-full items-center justify-center">
                <p className="bg-indigo-500 flex px-2 rounded-b-md text-white text-sm">
                  This event is still awaiting approval and is in draft mode
                </p>
              </div>
            </div>
          )}
          <CreateEventModal />
          <div className="flex">
            <Nav />
            {/* Main Content */}
            <motion.div
              initial={{
                marginLeft: navHidden ? "0rem" : "16rem",
                paddingTop: navHidden ? "0rem" : "1rem",
                paddingLeft: navHidden ? "0rem" : "1rem",
              }}
              animate={{
                marginLeft: navHidden ? "0rem" : "16rem",
                paddingTop: navHidden ? "0rem" : "1rem",
                paddingLeft: navHidden ? "0rem" : "1rem",
              }}
              transition={{ type: "spring", duration: 0.4 }}
              className="text-white flex-1"
            >
              <div className="min-h-full dark:bg-dark-primary bg-light-primary flex-1">
                <div className="flex flex-col">
                  <main
                    className={`relative h-screen overflow-y-scroll shadow-lg dark:bg-dark-secondary pb-20 ${
                      navHidden
                        ? ""
                        : "rounded-tr-md lg:rounded-tr-none lg:mr-0 rounded-tl-md border dark:border-white/5"
                    }`}
                  >
                    {workInProgress && (
                      <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-700 p-2 text-sm">
                        ⚠️ {workInProgress}
                      </div>
                    )}

                    {pageTitle && (
                      <div className="py-6 border-b border-gray-200 dark:border-white/5 px-4 py-4 flex sm:items-center sm:justify-between sm:px-6 lg:px-8">
                        <div className="min-w-0 flex-1 items-center flex">
                          <h1 className="text-lg font-medium leading-6 text-gray-900 dark:text-white sm:truncate">
                            {pageTitle}
                          </h1>
                        </div>

                        {rightControls && (
                          <div className="flex sm:ml-4 sm:mt-0">
                            {rightControls}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="">
                      {loading ? (
                        <div className="w-full h-screen flex items-center justify-center -mt-32">
                          <svg
                            className="animate-spin mr-2 h-12 w-12 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <>
                          {isOrganisationAdmin ? (
                            children
                          ) : (
                            <>
                              {requiresSuperuser ? (
                                <>
                                  {requiresSuperuser && isOrganisationAdmin ? (
                                    children
                                  ) : (
                                    <PermissionDenied />
                                  )}
                                </>
                              ) : (
                                <>
                                  {requiredPermissions.every((permission) =>
                                    currentPermissions.includes(permission)
                                  ) ? (
                                    children
                                  ) : (
                                    <PermissionDenied />
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </main>
                </div>
              </div>
            </motion.div>
          </div>
        </TooltipProvider>
      </ThemeProvider>
    </>
  );
};
